/**
 * Support contact helpers — WhatsApp click-to-chat and email.
 */

/** Fallback WTDcare unified number, overridable via VITE_CONTACT_PHONE. */
const DEFAULT_SUPPORT_PHONE = '920033297'

export const SUPPORT_PHONE: string =
  import.meta.env.VITE_CONTACT_PHONE || DEFAULT_SUPPORT_PHONE

export const SUPPORT_EMAIL: string =
  import.meta.env.VITE_CONTACT_EMAIL || 'info@wtd.care'

/**
 * Digits-only international number for wa.me / WhatsApp click-to-chat (no leading +).
 * Saudi 9200… toll/unified numbers start with "92", which WhatsApp otherwise parses as Pakistan (+92).
 */
export const toWhatsAppPhoneDigits = (raw: string): string => {
  const d = raw.replace(/\D/g, '')
  if (!d) return d
  if (d.startsWith('966')) return d
  // Pakistan mobile: 92 + operator digit + subscriber (avoid rewriting real PK numbers)
  if (d.startsWith('923') && d.length >= 12 && d.length <= 13) return d
  // Saudi unified / toll-free 9200 + 5 digits (9 digits total, e.g. 920033297)
  if (/^9200\d{5}$/.test(d)) return `966${d}`
  if (/^05\d{8}$/.test(d)) return `966${d.slice(1)}`
  if (/^5\d{8}$/.test(d)) return `966${d}`
  return d
}

/**
 * wa.me chat URL with an optional prefilled draft. `message` is passed unencoded —
 * URLSearchParams encodes it exactly once.
 */
export const buildWhatsAppUrl = (message?: string, phone = SUPPORT_PHONE): string => {
  const digits = toWhatsAppPhoneDigits(phone)
  const url = new URL(`https://wa.me/${digits}`)
  if (message) url.searchParams.set('text', message)
  return url.toString()
}

/** mailto: URL with an optional prefilled subject. */
export const buildSupportMailtoUrl = (subject?: string, email = SUPPORT_EMAIL): string => {
  if (!subject) return `mailto:${email}`
  return `mailto:${email}?subject=${encodeURIComponent(subject)}`
}
